import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import Icon from 'fe-design-base/atoms/Icon';
import Text from 'fe-design-base/atoms/Text';
import CardSelectable from 'fe-design-base/organisms/CardSelectable';

import { orderReasons } from 'features/scheduleBuilder/slices/reassignmentRecommendations/helper';
import {
  Reason,
  ReasonKey,
} from 'features/scheduleBuilder/slices/reassignmentRecommendations/types';
import { selectFullNameByJobId } from 'features/scheduleBuilder/slices/users/slice';

import { trackUxEvent } from 'util/tracking';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
  TRACK_ACTION_TYPES,
} from 'util/tracking_constants';

export const REASON_MAP = {
  role_match: {
    icon: 'Users',
    color: 'mono500',
  },
  on_time_arrival: {
    icon: 'AutoClockOut',
    color: 'mono500',
  },
  seniority: {
    icon: 'Handshake',
    color: 'mono500',
  },
  overtime: {
    icon: 'Warning',
    color: 'orange300',
  },
  conflicting_shift: {
    icon: 'Warning',
    color: 'orange300',
  },
};

interface Props {
  handleSelection?: (params: { jobId: string; selected: boolean }) => void;
  jobId: string;
  reasons: Array<Reason & { reason_key: ReasonKey }>;
  selectedEE?: string | null;
  rank?: number;
  available?: number;
}

export const ReassignmentSelectorCard = ({
  handleSelection,
  jobId,
  reasons,
  selectedEE,
  rank,
  available,
}: Props) => {
  const [isAnimating, setIsAnimating] = useState(true);

  const fullNameFromJobId = useSelector(state =>
    selectFullNameByJobId(state, jobId)
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimating(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const handleClick = useCallback(
    ({ value, selected }: { value: string; selected: boolean }) => {
      handleSelection?.({ jobId: value, selected });
      trackUxEvent({
        productArea: PRODUCT_AREAS.SCHEDULE,
        eventCategory: EVENT_CATEGORIES.SHIFT_REASSIGNMENT,
        eventAction: EVENT_ACTIONS.BUTTON_CLICKED,
        actionType: TRACK_ACTION_TYPES.CLICK,
        properties: {
          rank,
          available,
        },
      });
    },
    [handleSelection, rank, available]
  );

  return (
    <CardSelectable
      isAI
      isAIAnimating={isAnimating}
      title={fullNameFromJobId}
      value={jobId}
      data-testid="reassignment-selector-card"
      onClick={handleClick}
      minw={150}
      w={232}
      truncateTitle
      selected={selectedEE === jobId}
      radioCanDeselect
      content={
        <Box grow={1} h={54} pt={4} column>
          {reasons &&
            orderReasons(reasons).map(
              ({ reason_key, reason }) =>
                REASON_MAP[reason_key] && (
                  <Box row vcenter gap={8} key={reason_key}>
                    <Icon
                      iconName={REASON_MAP[reason_key].icon}
                      color={REASON_MAP[reason_key].color}
                      size="small"
                    />
                    <Text color="mono900" noWrap variant="captions">
                      {reason}
                    </Text>
                  </Box>
                )
            )}
        </Box>
      }
    />
  );
};

export default ReassignmentSelectorCard;
